import { ContentIcon, Metadata } from "@dropbox/dash-component-library";
import { Text } from "@dropbox/dig-components/typography";
import { Box, Split } from "@dropbox/dig-foundations";
import { Highlighter } from "@mirage/search/SearchResults/Highlighter";
import { DashTruncateWithTooltip } from "@mirage/shared/util/DashTruncateWithTooltip";
import cx from "classnames";
import React, { forwardRef } from "react";
import sharedStyles from "./shared.module.css";

export interface BaseResultRowProps {
  /** Icon to display on the left side of the row */
  icon: React.ReactNode;

  /** Text title to display in the row. Used when richTitle is not provided */
  title?: string;

  /** Whether to always bold the title. Equivalent to setting highlightWords to the title */
  titleAlwaysBold?: boolean;

  /** Custom React node to use as the title instead of the text title */
  richTitle?: React.ReactNode;

  /** Text or React node to display as a subtitle beside the title, separated by a middot */
  subtitle?: React.ReactNode;

  /** Array of additional metadata items to display beside the subtitle, separated by middots */
  additionalMetadata?: React.ReactNode[];

  /** Array of words to highlight in the title when using the Highlighter component */
  highlightWords?: string[];

  /** Optional accessory component to display on the right side of the row */
  accessory?: React.ReactNode;

  /** Whether to show the accessory component via CSS */
  showAccessory?: boolean;

  /** Whether to support empty title */
  shouldSupportEmptyTitle?: boolean;
}

export const BaseResultRow = forwardRef<HTMLDivElement, BaseResultRowProps>(
  (
    {
      icon,
      title,
      titleAlwaysBold,
      richTitle,
      subtitle,
      additionalMetadata,
      highlightWords,
      accessory,
      showAccessory,
      shouldSupportEmptyTitle = false,
    },
    ref,
  ) => {
    const renderMetadataElement = () => {
      if (!title?.length && !richTitle && subtitle && shouldSupportEmptyTitle) {
        return (
          <Box>
            <Text size="large" isBold={titleAlwaysBold}>
              {subtitle}
            </Text>
          </Box>
        );
      }

      return (
        <Metadata>
          <Metadata.Item>
            <Box className={sharedStyles.titleContainer}>
              <Text size="large" isBold={titleAlwaysBold}>
                {richTitle ? (
                  richTitle
                ) : highlightWords && title ? (
                  // NOTE: we don't currently support tooltips when we truncate titles
                  // since the highlighter component requires a string prop; will need to rework this to add this support
                  <Box
                    style={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    <Highlighter
                      fontSize="large"
                      title={title}
                      highlightWords={highlightWords}
                    />
                  </Box>
                ) : (
                  <DashTruncateWithTooltip lines={1} tooltipProps={{ title }}>
                    {title}
                  </DashTruncateWithTooltip>
                )}
              </Text>
            </Box>
          </Metadata.Item>

          {!!subtitle && (
            <Metadata.Item>
              <Box className={sharedStyles.subtitleContainer}>
                <Text color="subtle">{subtitle}</Text>
              </Box>
            </Metadata.Item>
          )}

          {!!additionalMetadata &&
            additionalMetadata.map((item: React.ReactNode, _: number) => (
              <Metadata.Item key={_}>{item}</Metadata.Item>
            ))}
        </Metadata>
      );
    };

    return (
      <Box
        ref={ref}
        display="flex"
        flexDirection="row"
        alignItems="center"
        justifyContent="space-between"
        className={sharedStyles.rowContainer}
      >
        <Box marginRight="Micro Medium" className={sharedStyles.iconContainer}>
          {icon}
        </Box>
        <Box flexGrow={1}>{renderMetadataElement()}</Box>

        {accessory && (
          <Box
            paddingLeft="Micro Small"
            className={cx(sharedStyles.accessoryBtnContainer, {
              [sharedStyles.showBtn]: showAccessory,
            })}
          >
            {accessory}
          </Box>
        )}
      </Box>
    );
  },
);

BaseResultRow.displayName = "BaseResultRow";

export const BaseResultSplitIconTitle = ({
  title,
  icon,
  itemTitle,
  highlightItemTitle,
}: {
  title: string;
  icon?: React.ReactNode;
  itemTitle?: string;
  highlightItemTitle?: boolean;
}) => {
  return (
    <Split alignY="center">
      <Split.Item paddingRight="Micro XSmall" style={{ flexShrink: 0 }}>
        {title}
      </Split.Item>

      {icon && (
        <Split.Item>
          <ContentIcon icon={icon} size="medium" hasBackground={false} />
        </Split.Item>
      )}

      {itemTitle && (
        <Split.Item
          style={{
            overflow: "hidden",
            fontWeight: highlightItemTitle ? 500 : undefined,
          }}
        >
          <DashTruncateWithTooltip
            lines={1}
            tooltipProps={{
              title: itemTitle,
            }}
          >
            {itemTitle}
          </DashTruncateWithTooltip>
        </Split.Item>
      )}
    </Split>
  );
};
